/**
 * Intrinsic pixel size read out of an image file's header — no decode. Measuring by
 * loading into an `<img>` costs a full decode; parsing the header reads a few KB, so
 * pending cards can take their real shape before their bytes are read.
 *
 * Covers the formats the pickers accept: PNG, JPEG, WebP, HEIC/HEIF. Returns null for
 * anything it can't parse, and callers fall back to a square placeholder.
 */

export interface ImagePixelSize {
  width: number
  height: number
}

/** Bytes per attempt. A JPEG's SOF sits after its EXIF block, which can carry a
 *  full-size thumbnail, so retry wider rather than always pay the large read. */
const HEADER_READ_SIZES = [64 * 1024, 512 * 1024]

/** Orientations that transpose the image. `<img>`/canvas apply orientation
 *  themselves, so the header size has to match. */
const TRANSPOSING_ORIENTATIONS = new Set([5, 6, 7, 8])

export async function readImageFileSizeFromHeader(file: File): Promise<ImagePixelSize | null> {
  try {
    let read = 0
    for (const readSize of HEADER_READ_SIZES) {
      if (read >= file.size) break
      const buffer = await file.slice(0, readSize).arrayBuffer()
      read = readSize
      const view = new DataView(buffer)
      const size = parsePng(view) ?? parseJpeg(view) ?? parseWebp(view) ?? parseIsoBmff(view)
      if (size && size.width > 0 && size.height > 0) return size
      // Only JPEG and HEIC can push their dimensions past the first read.
      if (view.byteLength < 2) break
      if (view.getUint16(0) !== 0xffd8 && !isIsoBmff(view)) break
    }
    return null
  } catch {
    return null
  }
}

/** PNG: 8-byte signature, then the IHDR chunk's width/height at a fixed offset. */
function parsePng(view: DataView): ImagePixelSize | null {
  if (view.byteLength < 24) return null
  if (view.getUint32(0) !== 0x89504e47 || view.getUint32(4) !== 0x0d0a1a0a) return null
  return { width: view.getUint32(16), height: view.getUint32(20) }
}

/** JPEG: walk the marker segments to the first SOF, picking up EXIF orientation from
 *  APP1 on the way (EXIF always precedes SOF) so a rotated photo reports its
 *  displayed shape. */
function parseJpeg(view: DataView): ImagePixelSize | null {
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null

  let orientation = 1
  let offset = 2
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) return null
    const marker = view.getUint8(offset + 1)
    // Standalone markers (no length field): padding, RSTn, SOI/EOI.
    if (marker === 0xff || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
      offset += 2
      continue
    }
    const length = view.getUint16(offset + 2)
    if (length < 2) return null

    // SOFn — every variant but DHT (0xC4), JPG (0xC8) and DAC (0xCC).
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      if (offset + 9 > view.byteLength) return null
      const height = view.getUint16(offset + 5)
      const width = view.getUint16(offset + 7)
      return TRANSPOSING_ORIENTATIONS.has(orientation)
        ? { width: height, height: width }
        : { width, height }
    }
    if (marker === 0xe1) {
      orientation = parseExifOrientation(view, offset + 4, length - 2) ?? orientation
    }
    offset += 2 + length
  }
  return null
}

/** Tag 0x0112 (Orientation) from an APP1 Exif segment's IFD0. */
function parseExifOrientation(view: DataView, start: number, length: number): number | null {
  // "Exif\0\0" then a TIFF header, whose byte order applies to everything after.
  if (length < 14 || start + 14 > view.byteLength) return null
  if (view.getUint32(start) !== 0x45786966 || view.getUint16(start + 4) !== 0x0000) return null
  const tiff = start + 6
  const byteOrder = view.getUint16(tiff)
  if (byteOrder !== 0x4949 && byteOrder !== 0x4d4d) return null
  const littleEndian = byteOrder === 0x4949
  if (view.getUint16(tiff + 2, littleEndian) !== 42) return null

  const ifdOffset = tiff + view.getUint32(tiff + 4, littleEndian)
  if (ifdOffset + 2 > view.byteLength) return null
  const entryCount = view.getUint16(ifdOffset, littleEndian)
  for (let i = 0; i < entryCount; i++) {
    const entry = ifdOffset + 2 + i * 12
    if (entry + 12 > view.byteLength) return null
    if (view.getUint16(entry, littleEndian) === 0x0112) {
      return view.getUint16(entry + 8, littleEndian)
    }
  }
  return null
}

/** An ISOBMFF file (HEIC/HEIF/AVIF) opens with an `ftyp` box. */
function isIsoBmff(view: DataView): boolean {
  return view.byteLength >= 12 && view.getUint32(4) === 0x66747970
}

/**
 * HEIC/HEIF (and AVIF): the size lives in an `ispe` box —
 * `[size][ispe][version+flags][width][height]`, big-endian. Rather than walk the box
 * tree down to `meta/iprp/ipco`, scan for the type and keep the largest result: one
 * `ispe` exists per item and the primary image is the biggest (the rest are
 * thumbnails and, in HDR files, gain maps).
 *
 * `ispe` holds the STORED extents, so `irot` has to be applied on top — a phone's
 * portrait shot is stored landscape.
 */
function parseIsoBmff(view: DataView): ImagePixelSize | null {
  if (!isIsoBmff(view)) return null
  let best: ImagePixelSize | null = null
  // 16 bytes of payload must follow the type for the box to hold a size.
  for (let offset = 8; offset + 20 <= view.byteLength; offset++) {
    if (view.getUint32(offset) !== 0x69737065) continue
    // The box length precedes the type and is always 20 for ispe. Checking it rules
    // out nearly every false hit from scanning bytes rather than walking the tree —
    // which matters because the largest hit wins.
    if (view.getUint32(offset - 4) !== 20) continue
    const width = view.getUint32(offset + 8)
    const height = view.getUint32(offset + 12)
    if (!width || !height) continue
    if (!best || width * height > best.width * best.height) best = { width, height }
  }
  if (!best) return null

  const rotation = parseIsoBmffRotation(view)
  return rotation === 1 || rotation === 3 ? { width: best.height, height: best.width } : best
}

/**
 * Rotation in counter-clockwise quarter turns (0-3). `irot` is a plain box, not a
 * FullBox, so the angle sits immediately after the type in the low two bits.
 *
 * Returns null when items disagree: pairing each `irot` with its item would mean
 * resolving `ipma` associations, and a captured photo's items share one orientation.
 * A file that breaks that gets the unrotated size, which the decode pass corrects.
 */
function parseIsoBmffRotation(view: DataView): number | null {
  let angle: number | null = null
  for (let offset = 4; offset + 5 <= view.byteLength; offset++) {
    if (view.getUint32(offset) !== 0x69726f74) continue
    const raw = view.getUint8(offset + 4)
    // Upper six bits are reserved zero; anything else is a false hit from scanning
    // bytes rather than walking the box tree.
    if ((raw & 0xfc) !== 0) continue
    const found = raw & 0x03
    if (angle !== null && angle !== found) return null
    angle = found
  }
  return angle
}

/** WebP: a RIFF container whose first chunk (VP8 / VP8L / VP8X) holds the size. */
function parseWebp(view: DataView): ImagePixelSize | null {
  if (view.byteLength < 30) return null
  if (view.getUint32(0) !== 0x52494646 || view.getUint32(8) !== 0x57454250) return null
  switch (view.getUint32(12)) {
    case 0x56503820: {
      // Lossy: keyframe header, then a 3-byte sync code before the dimensions.
      if (view.getUint8(23) !== 0x9d || view.getUint8(24) !== 0x01 || view.getUint8(25) !== 0x2a) {
        return null
      }
      return {
        width: view.getUint16(26, true) & 0x3fff,
        height: view.getUint16(28, true) & 0x3fff,
      }
    }
    case 0x5650384c: {
      // Lossless: 14 bits each for width-1 and height-1, after the 0x2f signature.
      if (view.getUint8(20) !== 0x2f) return null
      const bits = view.getUint32(21, true)
      return { width: (bits & 0x3fff) + 1, height: ((bits >> 14) & 0x3fff) + 1 }
    }
    case 0x56503858: {
      // Extended: canvas width-1 / height-1 as 24-bit little-endian values.
      const read24 = (at: number) =>
        view.getUint8(at) | (view.getUint8(at + 1) << 8) | (view.getUint8(at + 2) << 16)
      return { width: read24(24) + 1, height: read24(27) + 1 }
    }
    default:
      return null
  }
}
